/**
 * imageFileUtils (V3-S3) — the demo-honest drag & drop read pipeline for
 * product images: validate (MIME + size) → createImageBitmap → canvas
 * downscale (max edge 800px) → JPEG q0.8 data URI.
 *
 * The canvas/bitmap calls are INJECTED (`ImageReadDeps`) so the arithmetic
 * and validators run in the plain-node vitest env (vitest.config.ts — no
 * jsdom); only the default deps touch `document`/`createImageBitmap`, and
 * only when the browser caller actually invokes them.
 */

/** Source-file ceiling — larger files are rejected to the error tile. */
export const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024;

export const ACCEPTED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** `accept` attribute value for pickers/droppers — derived, never retyped. */
export const ACCEPTED_IMAGE_ACCEPT = ACCEPTED_IMAGE_MIME_TYPES.join(',');

export const IMAGE_DOWNSCALE_MAX_EDGE = 800;
export const IMAGE_DOWNSCALE_JPEG_QUALITY = 0.8;

/** Why a dropped file was refused (i18n keys: products:images.<reason>). */
export type ImageFileRejection = 'tooLarge' | 'notImage';

export const validateImageFile = (file: {
  type: string;
  size: number;
}): ImageFileRejection | null => {
  if (!(ACCEPTED_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) return 'notImage';
  if (file.size > MAX_IMAGE_FILE_BYTES) return 'tooLarge';
  return null;
};

/** Data-URI rows are demo uploads; provenance is derived, never stored. */
export const isDataUri = (url: string): boolean => url.startsWith('data:');

/**
 * Target dimensions after the max-edge downscale — aspect preserved, never
 * upscaled, each edge clamped to ≥ 1px so a degenerate ratio cannot produce
 * a zero-dimension (throwing) canvas.
 */
export const downscaleDimensions = (
  width: number,
  height: number,
  maxEdge: number = IMAGE_DOWNSCALE_MAX_EDGE
): { width: number; height: number } => {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height };
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

interface BitmapLike {
  width: number;
  height: number;
  close?: () => void;
}

interface CanvasLike {
  width: number;
  height: number;
  getContext: (id: '2d') => {
    drawImage: (image: BitmapLike, dx: number, dy: number, dw: number, dh: number) => void;
  } | null;
  toDataURL: (type: string, quality: number) => string;
}

export interface ImageReadDeps {
  createBitmap: (file: Blob) => Promise<BitmapLike>;
  createCanvas: () => CanvasLike;
}

const defaultDeps = (): ImageReadDeps => ({
  createBitmap: (file) => createImageBitmap(file),
  createCanvas: () => document.createElement('canvas') as unknown as CanvasLike,
});

/**
 * Read + downscale a dropped image into a JPEG data URI. The returned promise
 * ALWAYS settles (resolve or reject) — the caller maps rejection to the
 * error tile, so a failed read can never leave a spinner up (V3-S1 rule).
 */
export const readAndDownscaleImage = async (
  file: Blob,
  deps: ImageReadDeps = defaultDeps()
): Promise<string> => {
  const bitmap = await deps.createBitmap(file);
  try {
    const target = downscaleDimensions(bitmap.width, bitmap.height);
    const canvas = deps.createCanvas();
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas 2d context unavailable');
    context.drawImage(bitmap, 0, 0, target.width, target.height);
    return canvas.toDataURL('image/jpeg', IMAGE_DOWNSCALE_JPEG_QUALITY);
  } finally {
    bitmap.close?.();
  }
};
